import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Star, Quote } from 'lucide-react';

// Styled Components
const SectionWrapper = styled.section`
  padding: 5rem 0;
  background: linear-gradient(135deg, #faf5ff 0%, #fce7f3 100%);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -10%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(147, 51, 234, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }

  &:after {
    content: '';
    position: absolute;
    bottom: -50%;
    right: -10%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(236, 72, 153, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 1;

  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }

  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  animation: fadeIn 0.8s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1rem;

  @media (min-width: 640px) {
    font-size: 3rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: #4b5563;
  line-height: 1.6;
`;

const TestimonialGrid = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TestimonialCard = styled.div`
  background: white;
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  border: 2px solid transparent;
  animation: slideInUp 0.6s ease-out;
  animation-delay: ${props => props.$delay || '0s'};
  animation-fill-mode: backwards;

  &:hover {
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15);
    transform: translateY(-10px);
    border-color: rgba(147, 51, 234, 0.2);
  }

  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(40px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const QuoteIcon = styled.div`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  opacity: 0.1;
  transition: all 0.3s ease;

  svg {
    width: 48px;
    height: 48px;
    color: #9333ea;
  }

  ${TestimonialCard}:hover & {
    opacity: 0.2;
    transform: rotate(10deg) scale(1.1);
  }
`;

const StarRow = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1rem;
`;

const StarIcon = styled.div`
  transition: transform 0.2s ease;
  animation: starPop 0.5s ease-out;
  animation-delay: ${props => props.$delay || '0s'};
  animation-fill-mode: backwards;

  svg {
    width: 20px;
    height: 20px;
    color: #fbbf24;
    fill: #fbbf24;
  }

  ${TestimonialCard}:hover & {
    transform: scale(1.2) rotate(15deg);
  }

  @keyframes starPop {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const TestimonialText = styled.p`
  color: #374151;
  margin-bottom: 1.5rem;
  font-style: italic;
  line-height: 1.75;
  position: relative;
  z-index: 1;
  font-size: 0.95rem;

  &:before {
    content: '"';
    color: #9333ea;
    font-size: 2rem;
    position: absolute;
    left: -0.5rem;
    top: -0.5rem;
    opacity: 0.3;
  }
`;

const TestimonialFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.$gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.25rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  ${TestimonialCard}:hover & {
    transform: scale(1.1);
  }
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled.p`
  font-weight: bold;
  color: #111827;
  margin-bottom: 0.25rem;
`;

const AuthorRole = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
`;

const RatingBadge = styled.div`
  background: linear-gradient(135deg, #9333ea 0%, #ec4899 100%);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 4px 6px -1px rgba(147, 51, 234, 0.3);

  svg {
    width: 14px;
    height: 14px;
  }
`;

// Component
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Nguyễn Thu Hà",
      role: "Khách hàng thân thiết",
      rating: 5,
      comment: "Dịch vụ tuyệt vời! Nhân viên nhiệt tình, tay nghề cao. Tôi rất hài lòng với kiểu tóc mới.",
      avatar: "NH",
      gradient: "linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)",
      verified: true,
      delay: "0.1s"
    },
    {
      name: "Trần Minh Anh",
      role: "Khách hàng VIP",
      rating: 5,
      comment: "Salon sạch sẽ, chuyên nghiệp. Làm nail rất đẹp và bền màu. Sẽ quay lại!",
      avatar: "TA",
      gradient: "linear-gradient(135deg, #9333ea 0%, #c084fc 100%)",
      verified: true,
      delay: "0.2s"
    },
    {
      name: "Lê Phương",
      role: "Khách hàng thường xuyên",
      rating: 5,
      comment: "Giá cả hợp lý, chất lượng tốt. Đội ngũ tư vấn nhiệt tình. Highly recommended!",
      avatar: "LP",
      gradient: "linear-gradient(135deg, #60a5fa 0%, #5eead4 100%)",
      verified: true,
      delay: "0.3s"
    }
  ];

  return (
    <SectionWrapper id="đánh giá">
      <Container>
        <SectionHeader>
          <SectionTitle>Khách Hàng Nói Gì</SectionTitle>
          <SectionSubtitle>
            Phản hồi từ những khách hàng thân thiết
          </SectionSubtitle>
        </SectionHeader>

        <TestimonialGrid>
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index} $delay={testimonial.delay}>
              <QuoteIcon>
                <Quote />
              </QuoteIcon>

              {testimonial.verified && (
                <RatingBadge>
                  <Star style={{ fill: 'currentColor' }} />
                  Đã xác thực
                </RatingBadge>
              )}

              <StarRow>
                {[...Array(testimonial.rating)].map((_, i) => (
                  <StarIcon key={i} $delay={`${0.1 * i}s`}>
                    <Star />
                  </StarIcon>
                ))}
              </StarRow>

              <TestimonialText>
                {testimonial.comment}
              </TestimonialText>

              <TestimonialFooter>
                <Avatar $gradient={testimonial.gradient}>
                  {testimonial.avatar}
                </Avatar>
                <AuthorInfo>
                  <AuthorName>{testimonial.name}</AuthorName>
                  <AuthorRole>{testimonial.role}</AuthorRole>
                </AuthorInfo>
              </TestimonialFooter>
            </TestimonialCard>
          ))}
        </TestimonialGrid>
      </Container>
    </SectionWrapper>
  );
};

export default TestimonialsSection;